import type { AxiosRequestConfig } from 'axios';
import urlcat from 'urlcat';
import type {
    ModuleConstructorProps,
    UsageFeatures,
    UsageOrganisation,
    UsageOrganisationList,
    UsageOrganisationSort,
    UsageOrganisationType,
    UsagePeriod,
    UsageSummary,
    UsageUser,
} from '../Types';

/** Internal admin usage analytics. Every call needs `admin.can_view_usage`. */
const Usage = ({ endpoints, httpClient }: ModuleConstructorProps) => ({
    summary: (period: UsagePeriod, config?: AxiosRequestConfig) =>
        httpClient.get<{ data: UsageSummary }>(urlcat(endpoints.usage.summary, period), config),
    organisations: (
        params: UsagePeriod & {
            type?: UsageOrganisationType;
            search?: string;
            page?: number;
            per_page?: number;
            /** Omitted: most active first. */
            sort?: UsageOrganisationSort;
            direction?: 'asc' | 'desc';
        },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.get<UsageOrganisationList>(
            urlcat(endpoints.usage.organisations, params),
            config,
        ),
    organisation: (
        type: UsageOrganisationType,
        id: number,
        period: UsagePeriod,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.get<{ data: UsageOrganisation }>(
            urlcat(endpoints.usage.organisation, { type, id, ...period }),
            config,
        ),
    user: (
        id: number,
        params: UsagePeriod & { page?: number; per_page?: number },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.get<{ data: UsageUser }>(
            urlcat(endpoints.usage.user, { id, ...params }),
            config,
        ),
    /** `role` narrows every figure to people acting as that role. */
    features: (
        period: UsagePeriod & { role?: UsageOrganisationType },
        config?: AxiosRequestConfig,
    ) => httpClient.get<{ data: UsageFeatures }>(urlcat(endpoints.usage.features, period), config),
});

export default Usage;
